/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/top_snippets.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Use `TOP_SNIPPETS` to extract the best snippets for a given query string from a text field.
 *
 * @example
 * FROM books
 * | EVAL snippets = TOP_SNIPPETS(description, "Tolkien")
 *
 * @example
 * FROM books
 * | WHERE MATCH(title, "Return")
 * | EVAL snippets = TOP_SNIPPETS(description, "Tolkien", { "num_snippets": 3, "num_words": 25 })
 *
 * @example
 * FROM books
 * | WHERE MATCH(title, "return")
 * | RERANK "Tolkien" ON TOP_SNIPPETS(description, "Tolkien", { "num_snippets": 3, "num_words": 25 }) WITH { "inference_id" : "test_reranker" }
 *
 * @example
 * FROM books
 * | WHERE MATCH(title, "Return")
 * | EVAL snippets = TOP_SNIPPETS(description, "Tolkien", { "num_snippets": 1, "num_words": 25, "highlight": true })
 *
 * @example
 * ROW chunks = ["Alice was beginning to get very tired of sitting by her sister on the bank. The white rabbit disappeared down a hole and Alice quickly jumped in after it. The rabbit hole went straight on like a tunnel for some way and then dipped suddenly.", "The Queen of Hearts ordered her soldiers to paint the white roses red."]
 * | EVAL snippets = TOP_SNIPPETS(chunks, "alice rabbit hole", {"num_words": 0, "highlight": true})
 *
 * @example
 * ROW content = CONCAT(
 *     "# Climate Change Solutions\\n\\n",
 *     "## Renewable Energy\\n",
 *     "Solar and wind power are becoming cost-competitive with fossil fuels. Investment in clean energy infrastructure creates jobs.\\n\\n",
 *     "## Carbon Capture\\n",
 *     "Direct air capture technology removes CO2 from the atmosphere. These systems require significant energy input but show promise for large-scale deployment.\\n\\n",
 *     "## Policy Changes\\n",
 *     "Government regulations can accelerate the transition to clean energy through carbon pricing and renewable energy mandates.")
 * | EVAL chunked_content = CHUNK(content, {"strategy": "recursive", "max_chunk_size": 30, "separators": ["##", "\\n\\n"]})
 * | EVAL snippets = TOP_SNIPPETS(chunked_content, "clean energy", {"num_words": 0, "num_snippets": 1, "highlight":true})
 * | KEEP snippets
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'top_snippets',
  signatures: [
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'query',
          type: 'keyword',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'query',
          type: 'keyword',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'num_snippets\', values=[3], description=\'The maximum number of matching snippets to return.\', type=[integer]}, {name=\'num_words\', values=[300], description=\'The maximum number of words to return in each snippet.\n\t\n{applies_to}`stack: preview 9.4`When set to 0, disables chunking entirely,\n the input field values are used as-is, which is\nuseful when the text has already been chunked.\n\', type=[integer]}, {name=\'highlight\', values=[true], description=\'When true, wraps matched query terms in the returned snippets with markup tags.\nDefaults to false.\n\', type=[boolean]}, {name=\'pre_tag\', values=[<em>], description=\'Opening tag for highlighted terms. Only applies when highlight is true.\nDefaults to `<em>`.\n\', type=[keyword]}, {name=\'post_tag\', values=[</em>], description=\'Closing tag for highlighted terms. Only applies when highlight is true.\nDefaults to `</em>`.\n\', type=[keyword]}, {name=\'encoder\', values=[default], description=\'Controls HTML encoding of snippet text before tagging: `default` (no encoding) or `html`.\nOnly applies when highlight is true. Defaults to `default`.\n\', type=[keyword]}, {name=\'order\', values=[score, none], description=\'Order of returned snippets: `score` (default, by relevance) or `none` (original text order).\n\', type=[keyword]}, {name=\'analyzer\', values=[english], description=\'Name of the analyzer to use for scoring and highlighting. When omitted, defaults to the standard\nanalyzer. The name must match a registered analyzer (prebuilt or plugin-contributed), such as\n`standard`, `whitespace`, `simple`, `keyword`, `english`, `french`, `german`, `spanish`, etc.\n\', type=[keyword]}',
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'query',
          type: 'keyword',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'query',
          type: 'keyword',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'num_snippets\', values=[3], description=\'The maximum number of matching snippets to return.\', type=[integer]}, {name=\'num_words\', values=[300], description=\'The maximum number of words to return in each snippet.\n\t\n{applies_to}`stack: preview 9.4`When set to 0, disables chunking entirely,\n the input field values are used as-is, which is\nuseful when the text has already been chunked.\n\', type=[integer]}, {name=\'highlight\', values=[true], description=\'When true, wraps matched query terms in the returned snippets with markup tags.\nDefaults to false.\n\', type=[boolean]}, {name=\'pre_tag\', values=[<em>], description=\'Opening tag for highlighted terms. Only applies when highlight is true.\nDefaults to `<em>`.\n\', type=[keyword]}, {name=\'post_tag\', values=[</em>], description=\'Closing tag for highlighted terms. Only applies when highlight is true.\nDefaults to `</em>`.\n\', type=[keyword]}, {name=\'encoder\', values=[default], description=\'Controls HTML encoding of snippet text before tagging: `default` (no encoding) or `html`.\nOnly applies when highlight is true. Defaults to `default`.\n\', type=[keyword]}, {name=\'order\', values=[score, none], description=\'Order of returned snippets: `score` (default, by relevance) or `none` (original text order).\n\', type=[keyword]}, {name=\'analyzer\', values=[english], description=\'Name of the analyzer to use for scoring and highlighting. When omitted, defaults to the standard\nanalyzer. The name must match a registered analyzer (prebuilt or plugin-contributed), such as\n`standard`, `whitespace`, `simple`, `keyword`, `english`, `french`, `german`, `spanish`, etc.\n\', type=[keyword]}',
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
  ],
  preview: true,
  snapshotOnly: false,
};

export default definition;
